import { useEffect, useState } from "react";
import { Loader2, Shield, Clock, AlertTriangle, CheckCircle2, XCircle, X, Ban, MessageSquare, Snowflake, FileQuestion, ArrowUpRight, UserX, Plus } from "lucide-react";
import AdminShell, { Panel, StatusPill, Btn } from "@/components/admin/AdminShell";
import AdminPager from "@/components/admin/AdminPager";
import KpiCard from "@/components/admin/KpiCard";
import { api } from "@/lib/api";
import { useAdminPager, unwrapAdminList } from "@/lib/useAdminPager";

const STATUS_TABS = [
  { k: "open",          label: "Open" },
  { k: "investigating", label: "Investigating" },
  { k: "resolved",      label: "Resolved" },
];

export default function P2PDisputes() {
  const [tab, setTab] = useState("open");
  const [rows, setRows] = useState(null);
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState("");
  const [activeDispute, setActiveDispute] = useState(null);
  const pager = useAdminPager();

  const load = async () => {
    try {
      const [r, s] = await Promise.all([
        api.get("/api/admin/p2p/disputes", {
          params: {
            status: tab,
            sort: tab === "resolved" ? "newest" : "oldest",
            skip: pager.skip,
            limit: pager.limit,
          },
        }),
        api.get("/api/admin/p2p/stats"),
      ]);
      const { items, total } = unwrapAdminList(r.data, "disputes");
      setRows(items);
      pager.setTotal(total);
      setStats(s.data);
    } catch (e) { setError(e.response?.data?.detail || e.message); }
  };
  useEffect(() => { pager.resetPage(); /* eslint-disable-next-line */ }, [tab]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab, pager.page]);
  useEffect(() => { const t = setInterval(load, 8000); return () => clearInterval(t); /* eslint-disable-next-line */ }, [tab, pager.page]);

  const claim = async (id) => {
    setBusy(id);
    try {
      await api.post(`/api/admin/p2p/disputes/${id}/assign`);
      load();
    } catch (e) { setError(e.response?.data?.detail || e.message); }
    finally { setBusy(null); }
  };

  return (
    <AdminShell title="P2P disputes" subtitle="Appeals raised on P2P orders, with chat and payment evidence.">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        <KpiCard label="Open Disputes" value={stats?.open_disputes ?? "—"} icon={Shield} variant="red" />
        <KpiCard label="Avg Resolution (30d)" value={stats ? `${stats.avg_resolution_minutes_30d} min` : "—"} icon={Clock} variant="blue" />
        <KpiCard label="Completed Today" value={stats?.completed_today ?? "—"} icon={CheckCircle2} variant="green" />
        <KpiCard label="Banned Users" value={stats?.banned_users ?? "—"} icon={Ban} variant="yellow" />
      </div>

      <Panel className="mb-5">
        <div className="flex flex-wrap gap-2 p-3">
          {STATUS_TABS.map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              className={`px-3 py-1.5 rounded-md text-[12px] font-semibold ${
                tab === t.k ? "bg-warning text-surface" : "text-zinc-300 hover:bg-zinc-800"
              }`}
              data-testid={`disp-tab-${t.k}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </Panel>

      {error && <div className="mb-3 p-3 rounded-md bg-red-500/10 text-red-300 text-[12px]">{error}</div>}

      <Panel>
        <div className="overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead className="text-zinc-400 text-left border-b border-zinc-800">
              <tr>
                <th className="px-4 py-3">ID</th>
                <th className="px-4 py-3">Order</th>
                <th className="px-4 py-3">Reason</th>
                <th className="px-4 py-3">Raised By</th>
                <th className="px-4 py-3 text-right">Amount</th>
                <th className="px-4 py-3">Age</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows === null ? (
                <tr><td colSpan="8" className="px-4 py-12 text-center text-zinc-500">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan="8" className="px-4 py-12 text-center text-zinc-500">No {tab} disputes.</td></tr>
              ) : rows.map((d) => (
                <tr key={d.dispute_id} className="border-b border-zinc-800/50" data-testid={`disp-row-${d.dispute_id}`}>
                  <td className="px-4 py-3 font-mono text-zinc-300">
                    {d.dispute_id.slice(-12)}
                    {d.auto_opened && <div className="text-[10px] text-amber-400 mt-0.5">AUTO-OPENED</div>}
                  </td>
                  <td className="px-4 py-3 font-mono text-zinc-400">{d.order_id.slice(-10)}</td>
                  <td className="px-4 py-3 text-zinc-200">{d.reason.replace(/_/g, " ")}</td>
                  <td className="px-4 py-3 text-zinc-300 capitalize">{d.raised_by_role}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-zinc-200 font-semibold">
                    {Number(d.crypto_amount).toFixed(2)} {d.asset}
                    <div className="text-[10px] text-zinc-500">₹{Number(d.fiat_amount).toLocaleString("en-IN")}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={d.sla_breached ? "text-red-300 font-semibold" : "text-zinc-400"}>
                      {d.age_minutes < 60 ? `${d.age_minutes}m` : `${Math.floor(d.age_minutes / 60)}h ${d.age_minutes % 60}m`}
                      {d.sla_breached && <AlertTriangle size={10} className="inline ml-1" />}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <StatusPill value={d.status} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      {d.status === "open" && (
                        <Btn onClick={() => claim(d.dispute_id)} disabled={busy === d.dispute_id} kind="primary" data-testid={`disp-claim-${d.dispute_id}`}>
                          {busy === d.dispute_id ? <Loader2 size={11} className="animate-spin" /> : null} Claim
                        </Btn>
                      )}
                      <Btn onClick={() => setActiveDispute(d.dispute_id)} kind="ghost" data-testid={`disp-open-${d.dispute_id}`}>
                        Review
                      </Btn>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <AdminPager
          page={pager.page}
          total={pager.total}
          pageSize={pager.pageSize}
          onPage={pager.setPage}
          testId="disputes-pager"
        />
      </Panel>
      {activeDispute && (
        <DisputeModal id={activeDispute} onClose={(refresh) => { setActiveDispute(null); if (refresh) load(); }} />
      )}
    </AdminShell>
  );
}


function DisputeModal({ id, onClose }) {
  const [data, setData] = useState(null);
  const [intel, setIntel] = useState(null);
  const [notes, setNotes] = useState([]);
  const [resolution, setResolution] = useState("release_to_buyer");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [actionMsg, setActionMsg] = useState("");
  const [newNote, setNewNote] = useState("");

  const load = async () => {
    if (!id) return;
    try {
      const [r, intelR, notesR] = await Promise.all([
        api.get(`/api/admin/p2p/disputes/${id}`),
        api.get(`/api/admin/p2p/disputes/${id}/intel`).catch(() => ({ data: null })),
        api.get(`/api/admin/p2p/disputes/${id}/notes`).catch(() => ({ data: { notes: [] } })),
      ]);
      setData(r.data);
      setIntel(intelR.data);
      setNotes(notesR.data?.notes || []);
    } catch (e) { setError(e.response?.data?.detail || e.message); }
  };
  useEffect(() => { if (id) load(); /* eslint-disable-next-line */ }, [id]);

  const resolve = async () => {
    if (note.trim().length < 5) { setError("Resolution note required (≥5 chars)"); return; }
    setBusy(true); setError("");
    try {
      await api.post(`/api/admin/p2p/disputes/${id}/resolve`, { resolution, note });
      onClose(true);
    } catch (e) { setError(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const banUser = async (userId) => {
    const reason = window.prompt("Ban reason:");
    if (!reason) return;
    try {
      await api.post(`/api/admin/p2p/users/${userId}/ban`, { duration_hours: 24, reason });
      load();
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };

  // -------- Phase 3 actions --------
  const addNote = async () => {
    const body = newNote.trim();
    if (!body) return;
    setBusy(true); setError("");
    try {
      await api.post(`/api/admin/p2p/disputes/${id}/notes`, { body });
      setNewNote("");
      load();
    } catch (e) { setError(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const freeze = async () => {
    const reason = window.prompt("Freeze reason (visible to both parties):");
    if (!reason || reason.length < 3) return;
    setBusy(true);
    try {
      await api.post(`/api/admin/p2p/disputes/${id}/freeze`, { reason });
      setActionMsg("Trade frozen. Both parties have been notified.");
      load();
    } catch (e) { setError(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const requestEvidence = async () => {
    const target = window.prompt("Request from (buyer / seller / both):", "both");
    if (!target || !["buyer", "seller", "both"].includes(target.toLowerCase())) return;
    const reqNote = window.prompt("What evidence do you need? (visible to user)");
    if (!reqNote || reqNote.length < 3) return;
    setBusy(true);
    try {
      await api.post(`/api/admin/p2p/disputes/${id}/request-evidence`,
                     { target: target.toLowerCase(), note: reqNote });
      setActionMsg(`Requested more evidence from ${target}.`);
      load();
    } catch (e) { setError(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const escalate = async () => {
    const priority = window.prompt("Priority (high / critical):", "high");
    if (!priority || !["high", "critical"].includes(priority.toLowerCase())) return;
    const escNote = window.prompt("Escalation note:");
    if (!escNote || escNote.length < 3) return;
    setBusy(true);
    try {
      await api.post(`/api/admin/p2p/disputes/${id}/escalate`,
                     { priority: priority.toLowerCase(), note: escNote });
      setActionMsg(`Escalated to ${priority} priority.`);
      load();
    } catch (e) { setError(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  const suspend = async (userId, label) => {
    const days = window.prompt(`Suspend ${label} for how many days?`, "7");
    if (!days || isNaN(Number(days))) return;
    const reason = window.prompt("Reason:");
    if (!reason || reason.length < 3) return;
    setBusy(true);
    try {
      await api.post(`/api/admin/p2p/users/${userId}/ban`,
                     { duration_hours: Number(days) * 24, reason });
      setActionMsg(`${label} suspended for ${days} day(s).`);
      load();
    } catch (e) { setError(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  if (!data) return null;
  const { dispute, order, messages, parties } = data;
  const isResolved = dispute.status === "resolved" || dispute.status === "rejected";

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-center p-4" onClick={() => onClose(false)}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-[1100px] bg-surface rounded-xl border border-zinc-800 max-h-[92vh] overflow-y-auto text-zinc-200">
        <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between">
          <div>
            <h3 className="text-[16px] font-semibold flex items-center gap-2">
              <Shield size={16} className="text-red-400" /> Dispute {dispute.dispute_id}
            </h3>
            <div className="text-[11px] text-zinc-500 mt-0.5">
              Order {dispute.order_id} · Reason: <span className="capitalize">{dispute.reason.replace(/_/g, " ")}</span> ·
              Raised by {dispute.raised_by_role}
            </div>
          </div>
          <button onClick={() => onClose(false)} className="p-1 text-zinc-400 hover:text-white" data-testid="disp-modal-close">
            <X size={18} />
          </button>
        </div>

        <div className="grid lg:grid-cols-[1fr,360px] gap-5 p-5">
          {/* Left: order + parties + chat */}
          <div className="space-y-4">
            <Section title="Order">
              <Row label="Status" value={order.status} />
              <Row label="Crypto" value={`${order.crypto_amount} ${order.asset}`} />
              <Row label="Fiat" value={`₹${Number(order.fiat_amount).toLocaleString("en-IN")}`} />
              <Row label="Price" value={`₹${order.price}`} />
              <Row label="Created" value={new Date(order.created_at).toLocaleString()} />
              {order.payment_method_snapshot && (
                <>
                  <Row label="Pay via" value={order.payment_method_snapshot.type} />
                  {order.payment_method_snapshot.upi_id && <Row label="UPI" mono value={order.payment_method_snapshot.upi_id} />}
                  {order.payment_method_snapshot.account_number && <Row label="Acct" mono value={order.payment_method_snapshot.account_number} />}
                </>
              )}
            </Section>

            <Section title="Buyer">
              <Party p={parties.buyer} onBan={banUser} />
            </Section>
            <Section title="Seller">
              <Party p={parties.seller} onBan={banUser} />
            </Section>

            {dispute.evidence_urls && dispute.evidence_urls.length > 0 && (
              <Section title={`Evidence from ${dispute.raised_by_role} (${dispute.evidence_urls.length})`}>
                <EvidenceGrid items={dispute.evidence_urls} />
              </Section>
            )}

            {order.payment_proof_url && (
              <Section title="Buyer's Original Payment Receipt">
                <EvidenceGrid items={[order.payment_proof_url]} />
                {order.buyer_paid_note && (
                  <div className="mt-2 text-[12px] text-zinc-300">
                    <span className="text-zinc-500">UTR / reference:</span>{" "}
                    <span className="font-mono">{order.buyer_paid_note}</span>
                  </div>
                )}
              </Section>
            )}

            <Section title={<span className="flex items-center gap-2"><MessageSquare size={13}/> Chat Replay</span>}>
              <div className="space-y-2 text-[12px] max-h-[260px] overflow-y-auto pr-2">
                {messages.length === 0 ? (
                  <div className="text-zinc-500 text-center py-4">No messages.</div>
                ) : messages.map((m) => (
                  <div key={m.message_id} className={`p-2 rounded-md ${m.sender_role === "system" ? "bg-amber-900/20 text-amber-300" : m.sender_role === "buyer" ? "bg-emerald-900/20" : "bg-blue-900/20"}`}>
                    <div className="text-[10px] uppercase tracking-wider opacity-60 mb-0.5">
                      {m.sender_role} · {new Date(m.created_at).toLocaleTimeString()}
                    </div>
                    {m.body && <div className="whitespace-pre-wrap break-words">{m.body}</div>}
                    {m.attachment_url && (
                      <a href={m.attachment_url} target="_blank" rel="noreferrer" className="text-primary underline text-[11px]">
                        View attachment
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </Section>
          </div>

          {/* Right: claim summary + Phase 3 admin tools + resolve panel */}
          <div className="space-y-4">
            <Section title="Claim">
              <div className="text-[12px] text-zinc-300 whitespace-pre-wrap leading-relaxed">
                {dispute.description}
              </div>
            </Section>

            {/* ---- Fraud Intelligence ---- */}
            {intel && <FraudIntelPanel intel={intel} />}

            {/* ---- Quick Action buttons ---- */}
            {!isResolved && (
              <Section title="Admin Actions">
                {actionMsg && (
                  <div className="mb-2 text-[11.5px] text-emerald-300 bg-emerald-900/30 rounded-md p-2">
                    {actionMsg}
                  </div>
                )}
                <div className="grid grid-cols-2 gap-1.5">
                  <ActionBtn icon={Snowflake}  label="Freeze Trade"      onClick={freeze}          tone="amber"  testid="disp-act-freeze" />
                  <ActionBtn icon={FileQuestion} label="Request Proof"   onClick={requestEvidence} tone="blue"   testid="disp-act-req-evidence" />
                  <ActionBtn icon={ArrowUpRight} label="Escalate"        onClick={escalate}        tone="red"    testid="disp-act-escalate" />
                  <ActionBtn icon={UserX}        label="Suspend Buyer"   onClick={() => suspend(dispute.buyer_id, "buyer")}  tone="zinc" testid="disp-act-suspend-buyer" />
                  <ActionBtn icon={UserX}        label="Suspend Seller"  onClick={() => suspend(dispute.seller_id, "seller")} tone="zinc" testid="disp-act-suspend-seller" />
                </div>
              </Section>
            )}

            {/* ---- Internal Notes (admins only) ---- */}
            <Section title={`Internal Notes (${notes.length})`}>
              <div className="space-y-1.5 max-h-[180px] overflow-y-auto pr-1 mb-2">
                {notes.length === 0 ? (
                  <div className="text-[12px] text-zinc-500 italic">No internal notes yet.</div>
                ) : notes.map((n, i) => (
                  <div key={i} className="rounded-md bg-background border border-zinc-800 p-2 text-[12px]" data-testid={`disp-note-${i}`}>
                    <div className="text-[10px] text-zinc-500 mb-1 flex justify-between gap-2">
                      <span className="font-mono truncate">{n.author}</span>
                      <span>{new Date(n.at).toLocaleString()}</span>
                    </div>
                    <div className="text-zinc-200 whitespace-pre-wrap break-words">{n.body}</div>
                  </div>
                ))}
              </div>
              <div className="flex gap-1.5">
                <input
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) addNote(); }}
                  placeholder="Add an internal note (Cmd/Ctrl + Enter)…"
                  className="flex-1 px-2 py-1.5 rounded-md bg-background border border-zinc-700 text-[12px]"
                  data-testid="disp-note-input"
                />
                <button
                  onClick={addNote}
                  disabled={!newNote.trim() || busy}
                  className="px-2 py-1.5 rounded-md brand-gradient text-primary-foreground text-[11px] font-semibold disabled:opacity-50 inline-flex items-center"
                  data-testid="disp-note-submit"
                >
                  <Plus size={11} />
                </button>
              </div>
            </Section>

            {!isResolved ? (
              <Section title="Resolve">
                <div className="space-y-3">
                  {[["release_to_buyer", "Release to Buyer", "Credits crypto (− taker fee) to buyer's wallet"],
                    ["refund_to_seller",  "Refund to Seller", "Returns crypto to seller's ad escrow"]].map(([k, lbl, sub]) => (
                    <label key={k} className={`block p-3 rounded-md border cursor-pointer ${
                      resolution === k ? "border-primary bg-blue-900/20" : "border-zinc-700"
                    }`}>
                      <input type="radio" checked={resolution === k} onChange={() => setResolution(k)} className="mr-2" data-testid={`disp-res-${k}`} />
                      <span className="font-semibold text-[13px]">{lbl}</span>
                      <div className="text-[11px] text-zinc-400 mt-0.5">{sub}</div>
                    </label>
                  ))}
                  <textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    rows={3}
                    minLength={5}
                    maxLength={500}
                    placeholder="Resolution note (required, visible in audit log)"
                    className="w-full px-3 py-2 rounded-md bg-surface border border-zinc-700 text-[13px]"
                    data-testid="disp-res-note"
                  />
                  {error && <div className="text-red-300 text-[12px]">{error}</div>}
                  <button
                    onClick={resolve}
                    disabled={busy || note.trim().length < 5}
                    className="w-full px-4 py-2.5 rounded-md bg-amber-500 text-zinc-900 font-semibold text-[13px] disabled:opacity-50 inline-flex items-center justify-center gap-2"
                    data-testid="disp-res-submit"
                  >
                    {busy && <Loader2 size={13} className="animate-spin" />}
                    Submit Resolution
                  </button>
                </div>
              </Section>
            ) : (
              <Section title="Resolution">
                <Row label="Outcome" value={dispute.resolution} />
                <Row label="By" value={dispute.resolved_by_admin_id} />
                <Row label="When" value={new Date(dispute.resolved_at).toLocaleString()} />
                <div className="mt-2 text-[12px] text-zinc-300 italic">"{dispute.resolution_note}"</div>
              </Section>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* -----------------------------------------------------------
   FraudIntelPanel — risk score + findings + multi-account drilldowns
   ----------------------------------------------------------- */
const SEV_TONE = {
  high:   "bg-red-900/40 text-red-300 border-red-700",
  medium: "bg-amber-900/30 text-amber-200 border-amber-700",
  low:    "bg-zinc-800 text-zinc-300 border-zinc-700",
};

function FraudIntelPanel({ intel }) {
  const score = intel.risk_score;
  const band = intel.risk_band;
  const ringColor = band === "high" ? "#F4455D"
                  : band === "medium" ? "#f59e0b"
                  : "#44D15B";
  const findings = intel.findings || [];
  const dupUtr = intel.duplicate_utr_orders || [];
  const ipBuyer = intel.multi_account?.by_ip_buyer || [];
  const ipSeller = intel.multi_account?.by_ip_seller || [];
  const upi = intel.multi_account?.by_upi || [];

  return (
    <div className="rounded-lg bg-surface border border-zinc-800" data-testid="disp-fraud-intel">
      <div className="px-3 py-2 border-b border-zinc-800 text-[11px] font-semibold uppercase tracking-wider text-zinc-400 flex items-center gap-2">
        <AlertTriangle size={12} className="text-amber-400" /> Fraud Intelligence
      </div>
      <div className="p-3 space-y-3">
        <div className="flex items-center gap-3">
          <div
            className="relative w-[64px] h-[64px] rounded-full flex items-center justify-center flex-shrink-0"
            style={{ background: `conic-gradient(${ringColor} ${score * 3.6}deg, #1F2937 0deg)` }}
          >
            <div className="absolute inset-[3px] rounded-full bg-surface flex flex-col items-center justify-center">
              <span className="text-[18px] font-semibold text-zinc-100 leading-none" data-testid="intel-score">{score}</span>
              <span className="text-[8px] uppercase tracking-wider text-zinc-500">risk</span>
            </div>
          </div>
          <div className="min-w-0">
            <div className={"inline-block px-2 py-0.5 rounded-full text-[10.5px] font-semibold uppercase tracking-wider " +
              (band === "high" ? "bg-red-500/25 text-red-300"
                : band === "medium" ? "bg-amber-500/25 text-amber-200"
                : "bg-emerald-500/25 text-emerald-300")}>
              {band} risk
            </div>
            <div className="text-[11.5px] text-zinc-400 mt-1">
              {findings.length} signal{findings.length !== 1 ? "s" : ""} detected
            </div>
          </div>
        </div>

        {findings.length > 0 && (
          <div className="space-y-1.5" data-testid="intel-findings">
            {findings.map((f, i) => (
              <div key={i} className={`rounded-md border px-2.5 py-1.5 text-[11.5px] ${SEV_TONE[f.severity]}`}>
                <div className="font-semibold">{f.label}</div>
                <div className="opacity-90">{f.detail}</div>
              </div>
            ))}
          </div>
        )}

        {dupUtr.length > 0 && (
          <details className="text-[11.5px]">
            <summary className="cursor-pointer text-amber-300 font-semibold select-none">
              Duplicate UTR — {dupUtr.length} other order(s)
            </summary>
            <div className="mt-1 space-y-1 pl-2 border-l-2 border-amber-700">
              {dupUtr.map((o, i) => (
                <div key={i} className="text-zinc-300">
                  <span className="font-mono">{o.order_id}</span> · ₹{o.fiat_amount} · {o.status}
                </div>
              ))}
            </div>
          </details>
        )}

        {(ipBuyer.length > 0 || ipSeller.length > 0) && (
          <details className="text-[11.5px]">
            <summary className="cursor-pointer text-red-300 font-semibold select-none">
              Same-IP accounts — {ipBuyer.length + ipSeller.length} match(es)
            </summary>
            <div className="mt-1 space-y-1 pl-2 border-l-2 border-red-700">
              {[...ipBuyer, ...ipSeller].map((u, i) => (
                <div key={i} className="text-zinc-300 truncate">
                  <span className="text-zinc-500">[{u.shared_with}]</span>{" "}
                  {u.email || u.user_id} · KYC:{" "}
                  <span className="text-zinc-400">{u.kyc_status || "—"}</span>
                </div>
              ))}
            </div>
          </details>
        )}

        {upi.length > 0 && (
          <details className="text-[11.5px]">
            <summary className="cursor-pointer text-red-300 font-semibold select-none">
              Same-UPI accounts — {upi.length} match(es)
            </summary>
            <div className="mt-1 space-y-1 pl-2 border-l-2 border-red-700">
              {upi.map((u, i) => (
                <div key={i} className="text-zinc-300 truncate font-mono">
                  {u.upi_id} · {u.user_id}
                </div>
              ))}
            </div>
          </details>
        )}
      </div>
    </div>
  );
}

const TONE_BTN = {
  amber: "bg-amber-500/15 text-amber-200 hover:bg-amber-500/25 border-amber-700/50",
  blue:  "bg-blue-500/15 text-blue-200 hover:bg-blue-500/25 border-blue-700/50",
  red:   "bg-red-500/15 text-red-200 hover:bg-red-500/25 border-red-700/50",
  zinc:  "bg-zinc-700/40 text-zinc-200 hover:bg-zinc-700/60 border-zinc-600/50",
};

function ActionBtn({ icon: Icon, label, onClick, tone, testid }) {
  return (
    <button
      onClick={onClick}
      className={`px-2 py-1.5 rounded-md border text-[11px] font-semibold inline-flex items-center justify-center gap-1 transition ${TONE_BTN[tone] || TONE_BTN.zinc}`}
      data-testid={testid}
    >
      <Icon size={11} /> {label}
    </button>
  );
}

function Section({ title, children }) {
  return (
    <div className="rounded-lg bg-surface border border-zinc-800">
      <div className="px-3 py-2 border-b border-zinc-800 text-[11px] font-semibold uppercase tracking-wider text-zinc-400">{title}</div>
      <div className="p-3">{children}</div>
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="grid grid-cols-[110px,1fr] gap-3 py-1 text-[12.5px] items-baseline">
      <span className="text-zinc-500">{label}</span>
      <span
        className={`text-zinc-100 break-all ${mono ? "font-mono" : ""}`}
        data-testid={`row-${String(label).toLowerCase().replace(/\s+/g, "-")}`}
      >
        {value !== undefined && value !== null && value !== "" ? value : <span className="text-zinc-600">—</span>}
      </span>
    </div>
  );
}

/* -----------------------------------------------------------
   EvidenceGrid — renders images, videos, PDFs and external URLs
   ----------------------------------------------------------- */
function EvidenceGrid({ items }) {
  const [lightbox, setLightbox] = useState(null); // { url, kind }

  const classify = (u) => {
    if (!u) return { kind: "link", label: "—" };
    if (u.startsWith("data:image/"))                return { kind: "image", label: "Image" };
    if (u.startsWith("data:video/"))                return { kind: "video", label: "Video" };
    if (u.startsWith("data:application/pdf"))       return { kind: "pdf",   label: "PDF" };
    if (/^https?:\/\//i.test(u))                    return { kind: "link",  label: "External link" };
    return { kind: "link", label: "File" };
  };

  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="dispute-evidence-grid">
        {items.map((u, i) => {
          const { kind, label } = classify(u);
          return (
            <div key={i}
              className="relative rounded-md overflow-hidden border border-zinc-800 bg-background hover:border-primary transition group"
              data-testid={`dispute-evidence-${i}`}
            >
              {kind === "image" && (
                <button
                  onClick={() => setLightbox({ url: u, kind })}
                  className="block w-full aspect-square"
                >
                  <img src={u} alt={`Evidence ${i + 1}`} className="w-full h-full object-cover" />
                </button>
              )}
              {kind === "video" && (
                <video
                  src={u}
                  controls
                  className="block w-full aspect-square bg-black object-contain"
                />
              )}
              {kind === "pdf" && (
                <a
                  href={u}
                  target="_blank"
                  rel="noreferrer"
                  className="flex flex-col items-center justify-center w-full aspect-square text-red-300 gap-1"
                  download={`evidence-${i + 1}.pdf`}
                >
                  <span className="text-[20px] font-semibold">PDF</span>
                  <span className="text-[10.5px]">Open / download</span>
                </a>
              )}
              {kind === "link" && (
                <a
                  href={u}
                  target="_blank"
                  rel="noreferrer"
                  className="flex flex-col items-center justify-center w-full aspect-square text-primary gap-1 px-2 text-center"
                >
                  <span className="text-[14px] font-semibold">URL</span>
                  <span className="text-[10px] text-zinc-400 break-all line-clamp-3">{u}</span>
                </a>
              )}
              <div className="absolute top-1 right-1 px-1.5 py-0.5 rounded bg-black/70 text-[9.5px] text-zinc-200 font-semibold uppercase tracking-wider">
                {label}
              </div>
            </div>
          );
        })}
      </div>
      {lightbox && (
        <div
          className="fixed inset-0 z-[90] bg-black/85 grid place-items-center p-4"
          onClick={() => setLightbox(null)}
          data-testid="dispute-evidence-lightbox"
        >
          {lightbox.kind === "image" && (
            <img
              src={lightbox.url}
              alt="Evidence full"
              className="max-w-[92vw] max-h-[92vh] rounded-md shadow-2xl"
            />
          )}
        </div>
      )}
    </>
  );
}

function Party({ p, onBan }) {
  const banned = p.is_banned_until && new Date(p.is_banned_until) > new Date();
  return (
    <div className="space-y-1 text-[12px]">
      <Row label="Email" value={p.email} />
      <Row label="Nickname" value={p.nickname} />
      <Row label="Trades" value={p.trades_total} />
      <Row label="Completion" value={`${p.completion_rate_30d}%`} />
      <Row label="Strikes 24h" value={p.cancellation_strikes_24h} />
      <Row label="Status" value={banned ? `Banned until ${new Date(p.is_banned_until).toLocaleString()}` : "Active"} />
      {!banned && (
        <button
          onClick={() => onBan(p.user_id)}
          className="mt-1 px-2 py-1 rounded text-[11px] font-semibold bg-red-900/30 text-red-300 hover:bg-red-900/50 inline-flex items-center gap-1"
          data-testid={`ban-${p.user_id}`}
        >
          <Ban size={11} /> Ban 24h
        </button>
      )}
    </div>
  );
}
